import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  rescueUnassignedDeviceInventoryQuery,
  type RescueUnassignedDeviceInventoryData,
} from '../../lib/rescueQueries';
import { clampInventoryPage, inventoryQueryInput, inventoryTotalPages } from '../../lib/inventoryPaging';
import { SkeletonRows } from '../../components/SkeletonRow/SkeletonRow';
import Button from '../../components/Button/Button';
import Pagination from '../../components/Pagination/Pagination';
import CollapsibleSection from './CollapsibleSection';
import TableHead from './TableHead';
import { STOCK_COLUMNS } from './StockTable';
import styles from './Devices.module.scss';

const EM_DASH = '—';

interface LostTableProps {
  pageSize: number;
  searchQuery: string;
  /** Inoperable collars aren't listed (only Fi support can restore them), so the header just counts them. */
  inoperableCount: number;
  onMarkFound: (moduleId: string) => void;
}

/** Collars the rescue marked lost. A lost collar is on no pet, so the stock query lists them. */
export default function LostTable({ pageSize, searchQuery, inoperableCount, onMarkFound }: LostTableProps) {
  const [page, setPage] = useState(1);
  const [prevSearch, setPrevSearch] = useState(searchQuery);
  let currentPage = page;
  if (prevSearch !== searchQuery) {
    setPrevSearch(searchQuery);
    setPage(1);
    currentPage = 1;
  }

  const input = useMemo(
    () => ({ ...inventoryQueryInput(searchQuery, currentPage, pageSize), onlyLifecycleStatus: 'LOST' }),
    [searchQuery, currentPage, pageSize],
  );
  const { data, previousData, loading, error, refetch } = useQuery<RescueUnassignedDeviceInventoryData>(
    rescueUnassignedDeviceInventoryQuery,
    // Marking a collar lost refetches only mounted queries, and this table isn't mounted on the Fosters
    // or Adoptions tab, so a cached page would be stale when the Devices tab comes back.
    { variables: { input }, fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true },
  );
  const result = (data ?? previousData)?.rescueUnassignedDeviceInventory;
  const devices = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;
  const totalPages = inventoryTotalPages(totalCount, pageSize);

  // Marking the last collar on the last page found removes that page from under the user.
  const clampedPage = clampInventoryPage(currentPage, totalPages);
  if (clampedPage !== currentPage) {
    setPage(clampedPage);
  }

  const colCount = STOCK_COLUMNS.length;
  const showSkeleton = loading && !result && !error;

  return (
    <CollapsibleSection
      title="Lost"
      count={totalCount}
      description="Devices marked lost. Mark one found when it turns up to return it to your stock."
      defaultCollapsed
      headerAction={<span className={styles.sectionHeaderNote}>{inoperableCount} inoperable</span>}
    >
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <TableHead columns={STOCK_COLUMNS} />
          <tbody>
            {error && devices.length === 0 ? (
              <tr>
                <td colSpan={colCount} className={styles.errorRow} role="alert">
                  <span className={styles.errorRowContent}>
                    <span>Could not load lost devices.</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Swallowed deliberately: the failure is already on `error`, which this row renders from.
                        refetch().catch(() => {});
                      }}
                    >
                      Retry
                    </Button>
                  </span>
                </td>
              </tr>
            ) : showSkeleton ? (
              <SkeletonRows count={3} columns={colCount} />
            ) : totalCount === 0 ? (
              <tr>
                <td colSpan={colCount} className={styles.emptyState}>
                  {searchQuery ? `No lost devices matching "${searchQuery}"` : 'No lost devices'}
                </td>
              </tr>
            ) : (
              devices.map((d) => (
                <tr key={d.moduleId}>
                  <td>
                    <div className={styles.deviceId}>{d.moduleId}</div>
                    <div className={styles.deviceType}>{d.type ?? EM_DASH}</div>
                  </td>
                  <td>
                    <Button variant="green" size="sm" onClick={() => onMarkFound(d.moduleId)}>
                      Mark Found
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={clampedPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={setPage}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
